<ng-container *ngIf="enabled && !onChatPage">
  <!-- No collapsed button here: chat is opened from `<app-chat-trigger>` in the top bar. This
       component still owns the panel, because the header is a stacking context and a panel
       rendered inside it could not lift above the rest of the page. -->

  <!-- Mid floats and drags; the minimised bar docks in the corner and Full is pinned to the
       viewport, so a dragged position only applies while draggable. -->
  <section
    class="chat-launcher__panel"
    #panelEl
    *ngIf="store.panelOpen()"
    [class.is-mobile]="isMobile"
    [class.chat-launcher__panel--mini]="isMini"
    [class.chat-launcher__panel--mid]="!isMobile && panelSize() === 'mid'"
    [class.chat-launcher__panel--full]="!isMobile && panelSize() === 'full'"
    [class.is-draggable]="isDraggable"
    [style.left.px]="isDraggable && dragLeft !== null ? dragLeft : null"
    [style.top.px]="isDraggable && dragTop !== null ? dragTop : null"
    [style.right]="isDraggable && dragLeft !== null ? 'auto' : null"
    [style.bottom]="isDraggable && dragTop !== null ? 'auto' : null">
    <!-- Drag handle in Mid; in the minimised bar the whole header restores the panel. -->
    <header
      class="chat-launcher__header"
      (pointerdown)="startDrag($event)"
      (click)="onBarClick($event)"
      [attr.title]="isMini ? 'Restore Chat' : null">
      <span class="chat-launcher__title">
        <i class="pi pi-comments"></i>
        Chat
        <span class="chat-launcher__unread" *ngIf="isMini && store.totalUnread() > 0">{{
          store.totalUnread() > 99 ? '99+' : store.totalUnread()
        }}</span>
        <span class="chat-launcher__bar-thread" *ngIf="isMini && store.activeId() && store.activeTitle()">{{ store.activeTitle() }}</span>
      </span>
      <div class="chat-launcher__header-tools">
        <!-- Phone only: there the sidebar is the compact one, which hides its own status switcher. -->
        <app-chat-status-menu *ngIf="isMobile" [compact]="true" align="right"></app-chat-status-menu>
        <!-- Standard window controls: each size shows only the moves out of it, never a button for where it already is. -->
        <ng-container *ngIf="!isMobile">
          <button type="button" *ngIf="!isMini" (click)="minimise()" title="Minimise" aria-label="Minimise chat">
            <i class="pi pi-minus"></i>
          </button>
          <button type="button" *ngIf="isMini" (click)="restore()" title="Restore" aria-label="Restore chat">
            <i class="pi pi-window-maximize"></i>
          </button>
          <button type="button" *ngIf="panelSize() === 'mid'" (click)="setSize('full')" title="Full Screen" aria-label="Full screen">
            <i class="pi pi-window-maximize"></i>
          </button>
          <button type="button" *ngIf="panelSize() === 'full'" (click)="setSize('mid')" title="Exit Full Screen" aria-label="Exit full screen">
            <i class="pi pi-window-minimize"></i>
          </button>
        </ng-container>
        <button type="button" (click)="close()" title="Close" aria-label="Close chat"><i class="pi pi-times"></i></button>
      </div>
    </header>

    <!-- Mid / Full: the full page's layout — list, thread and (Full only) Group Info side by side. -->
    <div class="chat-launcher__split" *ngIf="isSplit">
      <aside class="chat-launcher__split-sidebar">
        <app-chat-sidebar (createGroup)="openCreate()"></app-chat-sidebar>
      </aside>
      <main class="chat-launcher__split-main">
        <app-chat-conversation [infoOpen]="infoOpen" (back)="back()" (toggleInfo)="toggleInfo()" (manageGroup)="openManage()"></app-chat-conversation>
      </main>
      <aside class="chat-launcher__split-info" *ngIf="infoOpen && store.activeId() && store.activeDetail()?.type !== 'direct'">
        <app-chat-group-info (closed)="closeInfo()" (manage)="openManage('details')" (addMembers)="openManage('members')"></app-chat-group-info>
      </aside>
    </div>

    <!-- Phone: one column at a time. -->
    <div class="chat-launcher__body" *ngIf="isMobile">
      <!-- One column at a time: the list, or the conversation you picked from it -->
      <app-chat-sidebar *ngIf="!store.activeId()" [compact]="true" (createGroup)="openCreate()"></app-chat-sidebar>
      <app-chat-conversation
        *ngIf="store.activeId()"
        [compact]="true"
        [infoOpen]="infoOpen"
        (back)="back()"
        (toggleInfo)="toggleInfo()"
        (manageGroup)="openManage()"></app-chat-conversation>

      <!--
        Group Info as an overlay rather than a third column — 380px has no room for one, and this
        is the only route to the member list from the widget (unlike "Manage group", which is
        owner-only).
      -->
      <!-- `!== 'direct'` rather than `=== 'group'`: detail is null while a thread loads, and the
           panel has to stay mounted through that to show its spinner instead of vanishing. -->
      <div class="chat-launcher__info" *ngIf="infoOpen && store.activeId() && store.activeDetail()?.type !== 'direct'">
        <app-chat-group-info (closed)="closeInfo()" (manage)="openManage('details')" (addMembers)="openManage('members')"></app-chat-group-info>
      </div>
    </div>

    <!-- Reads as the last row of the list rather than as a toolbar button, which is what it is:
         one more thing you can open from here. -->
    <footer class="chat-launcher__footer" *ngIf="isMobile && !store.activeId() && !store.chatUnavailable()">
      <button type="button" (click)="openCreate()">
        <span class="chat-launcher__footer-icon"><i class="pi pi-users"></i></span>
        <span class="chat-launcher__footer-text">
          <span class="chat-launcher__footer-title">New Group</span>
          <span class="chat-launcher__footer-hint">Create a group chat</span>
        </span>
      </button>

      <!-- Master Admin only: quick-message several or all colleagues, online or not, as individual DMs. -->
      <button type="button" *ngIf="store.amMasterAdmin && store.onlinePeople().length" (click)="store.beginQuickMessage()">
        <span class="chat-launcher__footer-icon"><i class="pi pi-send"></i></span>
        <span class="chat-launcher__footer-text">
          <span class="chat-launcher__footer-title">Message Online ({{ store.onlinePeople().length }})</span>
          <span class="chat-launcher__footer-hint">Quick-message colleagues, online or not</span>
        </span>
      </button>
    </footer>
  </section>
</ng-container>

<!--
  Outside the `!onChatPage` guard above on purpose: a toast for a *different* conversation is
  still worth showing while the user reads one on the full page. It is suppressed at source
  instead — the store only raises one when the thread it belongs to is not on screen.

  For the same reason it is no longer hidden while the widget is open. That guard assumed an open
  panel was showing you the message, which is only true for the one thread it has selected: a
  message from anyone else arrived, raised a toast, and had it rendered into a hidden element —
  the "no notification came through" case, with the panel sitting open on a different chat.
-->
<app-chat-toast *ngIf="enabled"></app-chat-toast>

<app-chat-group-dialog
  *ngIf="dialogMode"
  [mode]="dialogMode"
  [group]="store.activeDetail()"
  [startTab]="dialogTab"
  (closed)="closeDialog()"></app-chat-group-dialog>

<!-- Master Admin only, opened from the footer's "Message Online" button via the store -->
<app-chat-quick-message-dialog *ngIf="enabled && !onChatPage && store.quickMessageOpen()"></app-chat-quick-message-dialog>

<!--
  Opened from a bubble deep in the transcript; the message itself is held on the store.

  Guarded by the same `enabled && !onChatPage` as the widget above, unlike the group dialog next to
  it: that one opens off launcher-local state that the page can never set, whereas `forwardSource`
  is global. Without the guard, this component — which stays mounted on `/chat` with only its UI
  hidden — would render a second copy of the dialog on top of the page's own.
-->
<app-chat-forward-dialog *ngIf="enabled && !onChatPage && store.forwardSource()"></app-chat-forward-dialog>

<!--
  The ringing banner, deliberately outside the `!onChatPage` guard above: a call has to reach you
  wherever you are, including on `/chat` itself, where this component stays mounted with only the
  widget hidden. That makes this the one instance in the app, so the page does not mount its own.
-->
<app-chat-incoming-call *ngIf="enabled"></app-chat-incoming-call>
